import { Route } from "react-router";
import { useGetCreditCardApplicationQuery } from "@lending/hooks/queries/CreditCardApplication/useGetCreditCardApplicationQuery";
import { AddIncome } from "@lending/pages/Common/AddIncome";
import { Address } from "@lending/pages/Common/Address";
import { ApplicationQuestions } from "@lending/pages/Common/ApplicationQuestions";
import { Disclosures } from "@lending/pages/Common/Disclosures";
import { EligibilitySelection } from "@lending/pages/Common/EligibilitySelection";
import { EmploymentDetails } from "@lending/pages/Common/EmploymentDetails";
import { LivingArrangement } from "@lending/pages/Common/LivingArrangement";
import { PersonalInfo as CreditCardPersonalInfo } from "@lending/pages/Common/PersonalInfo";
import { PlaidPiiReview } from "@lending/pages/Common/PlaidPrefill/PersonalInfoReview";
import { PlaidPhoneNumber } from "@lending/pages/Common/PlaidPrefill/PlaidPhoneNumber";
import { Submitted } from "@lending/pages/Common/Submitted";
import { ConfirmInfo } from "@lending/pages/CreditCard/ConfirmInfo";
import { CreditPull } from "@lending/pages/CreditCard/CreditPull";
import { GetStarted } from "@lending/pages/CreditCard/GetStarted";
import { Offers } from "@lending/pages/CreditCard/Offers";
import { SelectCard } from "@lending/pages/CreditCard/SelectCard";
import { loadCreditCardPrequalApp } from "@lending/utils/loadPrequalApp";
import { getCoApplicantRoutes } from "./coApplicantRoutes";
import { ApplicationLayout } from "./layouts/ApplicationLayout";
import { getCreditCardCanonicalRoutePolicy } from "./layouts/creditCardRoutePolicy";
import { MainLayout } from "./MainLayout/MainLayout";
import { getMembershipShareRoutes } from "./membershipShareRoutes";
import { ProgressBarLayout } from "./ProgressBarLayout/ProgressBarLayout";

const webCreditCardRoutes = (
  <>
    <Route path="eligibility" element={<EligibilitySelection />} />
    <Route path="questions" element={<ApplicationQuestions />} />
    <Route path="get-started" element={<GetStarted />} />

    <Route
      path=":applicationUuid"
      element={
        <ApplicationLayout
          fallbackPath="/error"
          productType="credit-card"
          useApplicationQuery={useGetCreditCardApplicationQuery}
          getRoutePolicy={getCreditCardCanonicalRoutePolicy}
        />
      }
    >
      <Route index element={null} />
      <Route path="eligibility" element={<EligibilitySelection />} />
      <Route path="questions" element={<ApplicationQuestions />} />
      <Route path="get-started" element={<GetStarted />} />
    </Route>

    <Route element={<MainLayout flowType="web" />}>
      <Route element={<ProgressBarLayout />}>
        <Route path="select-card" element={<SelectCard />} />
        <Route path="phone" element={<PlaidPhoneNumber />} />
        <Route path="personal-info-review" element={<PlaidPiiReview />} />
        <Route path="personal-info" element={<CreditCardPersonalInfo />} />
        <Route
          path=":applicationUuid"
          element={
            <ApplicationLayout
              fallbackPath="/product-selection"
              productType="credit-card"
              useApplicationQuery={useGetCreditCardApplicationQuery}
              getRoutePolicy={getCreditCardCanonicalRoutePolicy}
            />
          }
        >
          <Route index element={null} />
          <Route path="phone" element={<PlaidPhoneNumber />} />
          <Route path="personal-info-review" element={<PlaidPiiReview />} />
          <Route path="select-card" element={<SelectCard />} />
          <Route path="personal-info" element={<CreditCardPersonalInfo />} />
          <Route path="address" element={<Address />} />
          <Route path="living-arrangement" element={<LivingArrangement />} />
          <Route path="add-income" element={<AddIncome />} />
          <Route path="employment-details" element={<EmploymentDetails />} />
          {getCoApplicantRoutes({
            productType: "credit_cards",
            fallbackPath: "/credit-card/:applicationUuid/disclosures",
          })}
          <Route path="disclosures" element={<Disclosures />} />
        </Route>
      </Route>
      <Route
        path=":applicationUuid"
        element={
          <ApplicationLayout
            fallbackPath="/product-selection"
            productType="credit-card"
            useApplicationQuery={useGetCreditCardApplicationQuery}
            getRoutePolicy={getCreditCardCanonicalRoutePolicy}
          />
        }
      >
        <Route index element={null} />
        <Route path="credit-pull" element={<CreditPull />} />
        <Route path="offers" element={<Offers />} />
        {getMembershipShareRoutes({ fallbackPath: "/credit-card/:applicationUuid/submitted" })}
        <Route path="submitted" element={<Submitted />} />
      </Route>
    </Route>
  </>
);

const olbCreditCardRoutes = (
  <>
    <Route path="confirm-info" loader={loadCreditCardPrequalApp} />
    <Route
      path=":applicationUuid"
      element={
        <ApplicationLayout
          fallbackPath="/olb-credit-card/confirm-info"
          productType="credit-card"
          useApplicationQuery={useGetCreditCardApplicationQuery}
          getRoutePolicy={getCreditCardCanonicalRoutePolicy}
        />
      }
    >
      <Route index element={null} />
      <Route element={<MainLayout flowType="olb" />}>
        <Route element={<ProgressBarLayout />}>
          <Route path="confirm-info" element={<ConfirmInfo />} />
          <Route path="select-card" element={<SelectCard />} />
          <Route path="personal-info" element={<CreditCardPersonalInfo />} />
          <Route path="address" element={<Address />} />
          <Route path="living-arrangement" element={<LivingArrangement />} />
          <Route path="add-income" element={<AddIncome />} />
          <Route path="employment-details" element={<EmploymentDetails />} />
          {getCoApplicantRoutes({
            productType: "credit_cards",
            fallbackPath: "/olb-credit-card/:applicationUuid/disclosures",
          })}
          <Route path="disclosures" element={<Disclosures />} />
        </Route>

        <Route path="credit-pull" element={<CreditPull />} />
        <Route path="offers" element={<Offers />} />
        <Route path="submitted" element={<Submitted />} />
      </Route>
    </Route>
  </>
);

export { webCreditCardRoutes, olbCreditCardRoutes };
